import { BaseFeeTiming, BillingPricingMode } from "@api/shared-runtime/enums";
import type { RouterOutputs } from "@/trpc";
import { formatPlanCurrency } from "@/components/billing-plan/billing-plan-values";

export type CurrentCycleUsage = NonNullable<
  RouterOutputs["organization"]["getCurrentCycleUsage"]
>;
export type UsageCategory = CurrentCycleUsage["categories"][number];

const BILLING_DISPLAY_TIMEZONE = "America/New_York";
const MAX_QUANTITY_FRACTION_DIGITS = 4;
const PERCENT_SUFFIX = "%";
const EM_DASH = "—";

export const PERCENT_BASIS_LABEL = "of approved wages";
export const BASE_FEE_LABEL = "Base fee";
export const NOT_BILLED_LABEL = "Not billed";
export const OVER_INCLUDED_LABEL = "Over included";
export const NO_CHARGE_LABEL = "No charge";
export const INCLUDED_IN_PLAN_LABEL = "Included in your plan";
export const UNUSED_BASE_FEE_DETAIL = "Per plan terms";
export const NO_BASE_FEE_DETAIL = "No base fee on this plan";
export const NO_USAGE_NOTICE = "No billable usage so far this cycle.";
export const UNPRICED_DETAIL =
  "Tracked for reference; not part of your plan's charges";
export const ESTIMATED_TOTAL_LABEL = "Estimated total so far";
export const REFERENCE_TOTAL_LABEL = "Plan total for this cycle (reference)";
export const TABLE_CAPTION =
  "Estimated charges and usage for the current billing cycle";
export const NO_PLAN_DESCRIPTION =
  "Usage and estimated charges for the current cycle.";
export const REPORTING_ONLY_TITLE = "Usage reporting only";
export const REPORTING_ONLY_BODY =
  "Saile is not invoicing this usage through the app this cycle. The figures below show your plan's terms applied to your usage, for your reference.";
export const USAGE_LOAD_ERROR =
  "Couldn't load this cycle's usage. Try refreshing the page.";

export const BASE_FEE_TIMING_DETAILS: Record<BaseFeeTiming, string> = {
  [BaseFeeTiming.InAdvance]: "Billed at the start of this cycle",
  [BaseFeeTiming.InArrears]: "Billed after this cycle ends",
};

/** Never promises an invoice date or automatic charging; the Saile team prepares invoices. */
export function describeTimingNote({
  baseFeeTiming,
  renewalDateLabel,
}: {
  baseFeeTiming: BaseFeeTiming;
  renewalDateLabel: string;
}): string {
  if (baseFeeTiming === BaseFeeTiming.InArrears) {
    return "The invoice for this cycle, covering the base fee and this cycle's usage, is prepared by the Saile team after the cycle ends. Amounts may change until then.";
  }
  return `This cycle's base fee was already billed at the start of the cycle. This cycle's usage is billed on your next cycle's invoice, together with that cycle's base fee. The next cycle begins ${renewalDateLabel}.`;
}

export function describeCycleEstimate({
  period,
  unitName,
  invoiced,
}: {
  period: string;
  unitName: string;
  invoiced: boolean;
}): string {
  const lead = invoiced ? "Estimate so far for" : "Reference figures for";
  return `${period}. ${lead} ${unitName}; usage can still change until the cycle ends.`;
}

const quantityFormatter = new Intl.NumberFormat("en-US", {
  maximumFractionDigits: MAX_QUANTITY_FRACTION_DIGITS,
});
const pluralRules = new Intl.PluralRules("en-US");

/** "42.0000" -> "42"; adds thousands grouping. */
export function formatQuantity(quantity: string | number): string {
  return quantityFormatter.format(Number(quantity));
}

export function formatQuantityWithNoun({
  quantity,
  noun,
}: {
  quantity: string | number;
  noun: string;
}): string {
  const label =
    pluralRules.select(Number(quantity)) === "one" ? noun : `${noun}s`;
  return `${formatQuantity(quantity)} ${label}`;
}

export function formatPercent(percent: string | number): string {
  return `${formatQuantity(percent)}${PERCENT_SUFFIX}`;
}

export function formatUpdatedAt(asOf: string): string {
  const formatted = new Intl.DateTimeFormat("en-US", {
    dateStyle: "medium",
    timeStyle: "short",
    timeZone: BILLING_DISPLAY_TIMEZONE,
  }).format(new Date(asOf));
  return `Updated ${formatted} ET`;
}

export type CategoryRate = {
  summary: string;
  /** Secondary muted text under the summary, e.g. the allowance remaining. */
  note: string | null;
  isOverage: boolean;
  isNotBilled: boolean;
};

function hasAllowance(line: UsageCategory): boolean {
  return (line.includedQuantity ?? 0) > 0;
}

function isOverage(line: UsageCategory): boolean {
  return (
    line.pricingMode === BillingPricingMode.PerUnit &&
    Number(line.billableQuantity ?? 0) > 0
  );
}

function isFreePerUnit(line: UsageCategory): boolean {
  return Number(line.unitAmount ?? 0) === 0;
}

function describePerUnitRate(line: UsageCategory): CategoryRate {
  const base = { note: null, isOverage: false, isNotBilled: false };
  if (isFreePerUnit(line)) {
    return { ...base, summary: INCLUDED_IN_PLAN_LABEL };
  }
  const price = formatPlanCurrency(line.unitAmount ?? "0");
  const included = line.includedQuantity ?? 0;
  if (!hasAllowance(line)) {
    return { ...base, summary: `${price} per ${line.unitNoun}` };
  }
  if (isOverage(line)) {
    return {
      ...base,
      isOverage: true,
      summary: `${formatQuantity(included)} included, then ${price} each`,
    };
  }
  const remaining = Math.max(included - Number(line.usedQuantity), 0);
  return {
    ...base,
    summary: `${formatQuantity(line.usedQuantity)} of ${formatQuantity(included)} included`,
    note: `${formatQuantity(remaining)} included left`,
  };
}

export function describeCategoryRate(line: UsageCategory): CategoryRate {
  if (line.pricingMode === BillingPricingMode.PerUnit) {
    return describePerUnitRate(line);
  }
  const base = { note: null, isOverage: false };
  if (line.pricingMode === BillingPricingMode.PercentOfBasis) {
    const summary =
      Number(line.basisPercent ?? 0) === 0
        ? NO_CHARGE_LABEL
        : `${formatPercent(line.basisPercent ?? "0")} ${PERCENT_BASIS_LABEL}`;
    return { ...base, summary, isNotBilled: false };
  }
  return { ...base, summary: NOT_BILLED_LABEL, isNotBilled: true };
}

/** How the amount was reached; null when the rate text already says everything. */
export function describeCategoryBreakdown(line: UsageCategory): string | null {
  if (line.pricingMode === null) {
    return UNPRICED_DETAIL;
  }
  if (line.pricingMode !== BillingPricingMode.PerUnit || isFreePerUnit(line)) {
    return null;
  }
  const price = formatPlanCurrency(line.unitAmount ?? "0");
  if (isOverage(line)) {
    const billable = formatQuantity(line.billableQuantity ?? "0");
    return `${billable} over included → ${billable} × ${price}`;
  }
  if (!hasAllowance(line)) {
    return `${formatQuantity(line.usedQuantity)} × ${price}`;
  }
  return null;
}

/** Billed lines read first; the API order is otherwise kept. */
export function sortCategoriesBilledFirst(
  categories: UsageCategory[],
): UsageCategory[] {
  return [
    ...categories.filter((line) => line.lineAmount !== null),
    ...categories.filter((line) => line.lineAmount === null),
  ];
}

export type UsageRow = {
  key: string;
  item: string;
  used: string;
  rate: CategoryRate | null;
  detail: string | null;
  amount: string;
  /** 0..1 share of the allowance used; null when the line has no allowance. */
  meterRatio: number | null;
};

function buildBaseFeeRow(usage: CurrentCycleUsage): UsageRow {
  let detail = BASE_FEE_TIMING_DETAILS[usage.baseFeeTiming];
  if (Number(usage.baseMonthlyAmount) === 0) {
    detail = NO_BASE_FEE_DETAIL;
  } else if (!usage.invoiced) {
    detail = UNUSED_BASE_FEE_DETAIL;
  }
  return {
    key: "base-fee",
    item: BASE_FEE_LABEL,
    used: EM_DASH,
    rate: null,
    detail,
    amount: formatPlanCurrency(usage.baseMonthlyAmount),
    meterRatio: null,
  };
}

function buildCategoryRow(line: UsageCategory): UsageRow {
  const included = line.includedQuantity ?? 0;
  const showMeter =
    line.pricingMode === BillingPricingMode.PerUnit && included > 0;
  return {
    key: line.categoryCode,
    item: line.displayName,
    used: formatQuantityWithNoun({
      quantity: line.usedQuantity,
      noun: line.unitNoun,
    }),
    rate: describeCategoryRate(line),
    detail: describeCategoryBreakdown(line),
    amount:
      line.lineAmount === null
        ? NOT_BILLED_LABEL
        : formatPlanCurrency(line.lineAmount),
    meterRatio: showMeter
      ? Math.min(Number(line.usedQuantity) / included, 1)
      : null,
  };
}

export type UsageTotals = {
  saileCharges: string;
  wagePassThrough: string | null;
  total: string;
};

export function buildUsageTotals(usage: CurrentCycleUsage): UsageTotals {
  return {
    saileCharges: formatPlanCurrency(usage.saileChargesAmount),
    wagePassThrough:
      usage.wagePassThroughAmount === null
        ? null
        : formatPlanCurrency(usage.wagePassThroughAmount),
    total: formatPlanCurrency(usage.totalAmount),
  };
}

export function buildUsageRows(usage: CurrentCycleUsage): UsageRow[] {
  const categoryRows = sortCategoriesBilledFirst(usage.categories).map(
    buildCategoryRow,
  );
  const hideBaseFee =
    Number(usage.baseMonthlyAmount) === 0 &&
    categoryRows.length === 0 &&
    usage.wagePassThroughAmount === null;
  return hideBaseFee ? categoryRows : [buildBaseFeeRow(usage), ...categoryRows];
}
